import React, { useState, useEffect, useRef } from "react";
import { getTax, extractTaxDataFromRatePlan, isTaxIncluded, hasIncludedTax } from "../../services/taxApi";
import { getLoyaltyDiscount } from "../../utils/auth";
import { getBreakfastStatus } from "../../utils/breakfast";
import { useTranslation } from "../../context/TranslationContext";
import { trackMemberUnlock } from "../../services/analyticsService";


const RatePlanCard = ({ room, plan, currentQuantity, updateRoomQuantity, selectedRooms = [], guests = { adults: 1, children: 0 }, childAges = [], selectedDates, calculateNights, accommodationDetail, currentCurrency, onOpenLogin, isLoggedIn = false, memberDiscountPercent = 0, isLowestRate = false }) => {
  const { t } = useTranslation();
  // Helper function to round currency values to 2 decimal places (preserves precision for non-INR currencies like USD)
  const roundCurrency = (value) => Math.round(value * 100) / 100;

  // Helper to check if a rate plan has any real policy data
  const hasAnyPolicyData = (rp) => {
    if (!rp) return false;
    const isValid = (val) => val && !Array.isArray(val) && String(val).trim() && String(val).trim().toLowerCase() !== 'free cancellation';
    return !!(isValid(rp.bookingPolicy) || isValid(rp.cancellationPolicy) || isValid(rp.noShowPolicy) || (isValid(rp.noShowPolicyType) && String(rp.noShowPolicyType).trim().toLowerCase() !== 'free'));
  };

  // State for Booking Policy tooltip
  const [showPolicyTooltip, setShowPolicyTooltip] = useState(false);
  const policyTooltipRef = useRef(null);
  
  // Close tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (policyTooltipRef.current && !policyTooltipRef.current.contains(event.target)) {
        setShowPolicyTooltip(false);
      }
    };

    if (showPolicyTooltip) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPolicyTooltip]);
  
  // Check if user selected currency is different from accommodation API currency
  const useConvertedCurrency = accommodationDetail && 
                                currentCurrency && 
                                accommodationDetail.currencyCode && 
                                currentCurrency.code && 
                                currentCurrency.code.toUpperCase() !== accommodationDetail.currencyCode.toUpperCase();
  
  // Check for convertedRate value - if available and valid, use it
  let convertedRateValue = null;
  if (plan.convertedRate) {
    convertedRateValue = parseFloat(plan.convertedRate);
  } 
  // If not available in plan, check rateDetailsByDate array
  else if (plan.rateDetailsByDate && Array.isArray(plan.rateDetailsByDate) && plan.rateDetailsByDate.length > 0) {
    const firstRateDetail = plan.rateDetailsByDate[0];
    if (firstRateDetail.convertedRate) {
      convertedRateValue = parseFloat(firstRateDetail.convertedRate);
    }
  }
  
  // Check if we're using convertedRate
  const isUsingConvertedRate = convertedRateValue && !isNaN(convertedRateValue) && convertedRateValue > 0;

  // Helper to check if value is a valid non-empty string (not array, not empty)
  const isValidCurrency = (val) => typeof val === 'string' && val.trim() !== '';

  // Helper to convert currency code to symbol
  const codeToSymbol = (code) => {
    if (!code || typeof code !== 'string') return '';
    const symbols = {
      'INR': '₹', 'USD': '$', 'EUR': '€', 'GBP': '£', 'AED': 'AED',
      'SAR': 'SAR', 'QAR': 'QAR', 'KWD': 'KWD', 'OMR': 'OMR', 'BHD': 'BHD',
      'PHP': '₱', 'THB': '฿', 'MYR': 'RM', 'SGD': 'S$', 'IDR': 'Rp',
      'PKR': 'Rs'
    };
    return symbols[code.toUpperCase()] || code;
  };

  // Currency priority: RatePlanCurrencyCode > CurrencyCode > currency > convertedCurrency
  // Use convertedCurrency only if convertedRate is being used and it's a valid string
  const rawCurrencyCode = isUsingConvertedRate && isValidCurrency(plan.convertedCurrency)
    ? plan.convertedCurrency
    : (isValidCurrency(plan.RatePlanCurrency) ? plan.RatePlanCurrency
       : isValidCurrency(plan.ratePlanCurrency) ? plan.ratePlanCurrency
       : isValidCurrency(plan.ratePlanCurrencyCode) ? plan.ratePlanCurrencyCode
       : isValidCurrency(plan.CurrencyCode) ? plan.CurrencyCode
       : isValidCurrency(plan.currency) ? plan.currency
       : '');
  const rawSymbol = codeToSymbol(rawCurrencyCode) || currentCurrency?.symbol || '';
  // Add space after currency code (e.g., "KES ") but not after symbols (e.g., "$")
  const displayCurrency = rawSymbol.length > 1 ? rawSymbol + ' ' : rawSymbol;
  
  // Use convertedRate if available and valid, otherwise use normal price
  let originalPrice;
  if (isUsingConvertedRate) {
    // Use convertedRate if it has a valid value
    originalPrice = roundCurrency(convertedRateValue);
  } else {
    // Use plan.price directly if available, otherwise calculate from room.basePrice
    originalPrice = plan.price
      ? roundCurrency(plan.price)
      : roundCurrency(room.basePrice * (1 + (plan.priceModifier || 0)));
  }
  
  // Get loyalty discount if user is logged in
  const loyaltyDiscount = isLoggedIn ? getLoyaltyDiscount() : null;
  
  // Safely access loyalty discount values with null checks
  // API response structure: { success: true, data: { Value: 8, MaxValue: 15, Type: "percent", membership_type: {...} } }
  const loyaltyData = loyaltyDiscount && loyaltyDiscount.data ? loyaltyDiscount.data : null;
  const loyaltyDiscountValue = loyaltyData && loyaltyData.Value !== undefined && loyaltyData.Value !== null
    ? parseFloat(loyaltyData.Value) 
    : 0;
  // MaxValue comes dynamically from API response - if not provided, default to 0 (no limit)
  const loyaltyMaxValue = loyaltyData && loyaltyData.MaxValue !== undefined && loyaltyData.MaxValue !== null 
    ? parseFloat(loyaltyData.MaxValue) 
    : 0;
  const loyaltyType = loyaltyData && loyaltyData.Type ? loyaltyData.Type : 'percent';
  const membershipTypeName = loyaltyData && loyaltyData.membership_type && loyaltyData.membership_type.MemberShipTypeName 
    ? loyaltyData.membership_type.MemberShipTypeName 
    : null;
  
  // Check for promotion discount from availabilityApi - Promotion only applies if both name and discount value are present
  const hasPromotionFromAvailability = plan.promotionDiscount && 
    (plan.promotionDiscount.discount > 0 || plan.promotionDiscount.DiscountValue > 0) &&
    (plan.promotionDiscount.promotionName || plan.promotionName);
  
  // Use discountValue for percentage (this is the actual discount percentage to display)
  // discount is used for calculation, discountValue is the display value
  let promotionDiscountPercent = 0;
  if (hasPromotionFromAvailability) {
    const dtype = (plan.promotionDiscount.discountType || '').toLowerCase();
    const discountVal = parseFloat(plan.promotionDiscount.discountValue || plan.promotionDiscount.DiscountValue || 0);
    if (dtype.includes('percentage')) {
      // Percentage: discountValue is percentage (e.g., 41 = 41%)
      promotionDiscountPercent = Math.max(0, Math.min(100, discountVal));
    } else if (dtype.includes('fixed') && originalPrice > 0) {
      // Fixed: discountValue is amount — convert to percentage for display
      promotionDiscountPercent = Math.max(0, Math.min(100, (discountVal / originalPrice) * 100));
    } else {
      promotionDiscountPercent = Math.max(0, Math.min(100, discountVal));
    }
  }
  
  // Get discount from fetchRedeemOverrideRules API (if available and user is logged in)
  let redeemOverrideDiscountPercent = 0;
  if (isLoggedIn && loyaltyDiscountValue > 0 && loyaltyType === 'percent') {
    // Use loyalty discount from fetchRedeemOverrideRules API
    const validLoyaltyDiscount = Math.max(0, Math.min(100, loyaltyDiscountValue));
    redeemOverrideDiscountPercent = validLoyaltyDiscount;
  }
  
  // Calculate total discount based on conditions:
  // 1. If only availabilityApi discount exists -> use it
  // 2. If only fetchRedeemOverrideRules discount exists -> use it
  // 3. If both exist -> sum them
  let totalDiscountPercent = 0;
  
  if (hasPromotionFromAvailability && redeemOverrideDiscountPercent > 0) {
    // Both discounts exist - sum them
    totalDiscountPercent = promotionDiscountPercent + redeemOverrideDiscountPercent;
    
    // Apply MaxValue limit dynamically from API - if total exceeds MaxValue, cap at MaxValue
    if (loyaltyMaxValue > 0 && totalDiscountPercent > loyaltyMaxValue) {
      totalDiscountPercent = loyaltyMaxValue;
    }
  } else if (hasPromotionFromAvailability) {
    // Only availabilityApi discount exists
    totalDiscountPercent = promotionDiscountPercent;
  } else if (redeemOverrideDiscountPercent > 0) {
    // Only fetchRedeemOverrideRules discount exists
    totalDiscountPercent = redeemOverrideDiscountPercent;
    
    // Apply MaxValue limit dynamically from API - if total exceeds MaxValue, cap at MaxValue
    if (loyaltyMaxValue > 0 && totalDiscountPercent > loyaltyMaxValue) {
      totalDiscountPercent = loyaltyMaxValue;
    }
  }
  
  // Final safety check: ensure total discount doesn't exceed 100%
  if (totalDiscountPercent > 100) {
    totalDiscountPercent = 100;
  }
  
  // Final validation: ensure total discount is between 0 and 100
  totalDiscountPercent = Math.max(0, Math.min(100, totalDiscountPercent));

  console.log('[RatePlanCard] Raw promotionDiscount:', plan.promotionDiscount);
  console.log('[RatePlanCard] Discount Debug:', {
    planName: plan.name || plan.RatePlanName,
    originalPrice,
    promotionDiscountPercent,
    loyaltyDiscountValue,
    loyaltyMaxValue,
    loyaltyType,
    redeemOverrideDiscountPercent,
    totalDiscountPercent,
    hasPromotionFromAvailability,
    isLoggedIn,
    isUsingConvertedRate,
    convertedRateValue
  });

  // Calculate final price
  let calculatedPrice = originalPrice;
  
  // Check if we have any discount (from availabilityApi or fetchRedeemOverrideRules)
  const hasAnyDiscount = hasPromotionFromAvailability || redeemOverrideDiscountPercent > 0;
  
  if (hasAnyDiscount) {
    // Promotion discount exists
    if (isLoggedIn && loyaltyDiscountValue > 0 && loyaltyType === 'percent') {
      // User is logged in with loyalty discount - recalculate with promotion + loyalty
      if (useConvertedCurrency && convertedRateValue && !isNaN(convertedRateValue) && convertedRateValue > 0) {
        calculatedPrice = roundCurrency(convertedRateValue * (1 - (totalDiscountPercent / 100)));
      } else {
        // Apply total discount (promotion + loyalty) to original price
        calculatedPrice = roundCurrency(originalPrice * (1 - (totalDiscountPercent / 100)));
      }
    } else {
      // User is NOT logged in - use API's afterDiscountAmount directly (no recalculation needed)
      // But skip if convertedRate is being used (afterDiscountAmount is in original currency)
      if (hasPromotionFromAvailability && plan.promotionDiscount.afterDiscountAmount && !isUsingConvertedRate) {
        calculatedPrice = roundCurrency(plan.promotionDiscount.afterDiscountAmount);
      } else {
        // Fallback: calculate discount if afterDiscountAmount not available
        if (useConvertedCurrency && convertedRateValue && !isNaN(convertedRateValue) && convertedRateValue > 0) {
          calculatedPrice = roundCurrency(convertedRateValue * (1 - (totalDiscountPercent / 100)));
        } else {
          calculatedPrice = roundCurrency(originalPrice * (1 - (totalDiscountPercent / 100)));
        }
      }
    }
  }
  // If no promotion and not logged in (or no loyalty discount): calculatedPrice stays as originalPrice (normal rate)
 
  // Create enhanced promotion discount object for display
  // Get promotionName from plan.promotionDiscount or plan.promotionName (fallback)
  const promotionName = plan.promotionDiscount?.promotionName || plan.promotionName || '';
  
  const enhancedPromotionDiscount = hasPromotionFromAvailability ? {
    ...plan.promotionDiscount,
    promotionName: promotionName, // Ensure promotionName is included
    discount: totalDiscountPercent,
    originalDiscount: promotionDiscountPercent,
    loyaltyDiscount: loyaltyDiscountValue,
    afterDiscountAmount: calculatedPrice
  } : null;
  
  const maxGuests = plan.maxGuests || 2;
  const isSelected = currentQuantity > 0;
  
  // Calculate total quantity selected for this room across all rate plans
  const totalSelectedForRoom = selectedRooms
    .filter((item) => item.room.id === room.id)
    .reduce((sum, item) => sum + item.quantity, 0);
  
  // Get available rooms for this room type
  const roomsAvailable = room.totalroom || 0;
  
  // Check if increment button should be disabled
  const isMaxReached = totalSelectedForRoom >= roomsAvailable;

  // Tax modal state
  const [showTaxModal, setShowTaxModal] = useState(false);
  const [taxData, setTaxData] = useState(null);
  const [isLoadingTax, setIsLoadingTax] = useState(false);
  const [taxError, setTaxError] = useState(null);
  
  // Breakfast modal state
  const [showBreakfastModal, setShowBreakfastModal] = useState(false);
  const [showIncludedModal, setShowIncludedModal] = useState(false);

  // Calculate nights
  const nights = calculateNights ? calculateNights() : 1;

  // Check if tax exists and is greater than 0
  const hasTax = () => {
    // Check if plan has taxes array with items
    if (plan.taxes && Array.isArray(plan.taxes) && plan.taxes.length > 0) {
      // Check if any tax has a non-zero amount
      const hasNonZeroTax = plan.taxes.some(tax => {
        const taxAmount = tax.TaxAmmount || tax.TaxAmount || tax.taxAmount || tax.amount || 0;
        const taxValue = tax.TaxValue || tax.taxValue || tax.value || 0;
        return parseFloat(taxAmount) > 0 || parseFloat(taxValue) > 0;
      });
      return hasNonZeroTax;
    }
    // Check taxData if available
    if (taxData) {
      const totalTax = taxData.totalTax || taxData.TotalTax || taxData.totalTaxAmount || taxData.TotalTaxAmount || 0;
      return parseFloat(totalTax) > 0;
    }
    return false;
  };

  // Calculate tax amount per night for 1 room
  // Sums all taxes: service, GST, VAT, etc.
  const calculateTaxPerNight = () => {
    if (!plan.taxes || !Array.isArray(plan.taxes) || plan.taxes.length === 0) {
      return 0;
    }

    // Calculate total tax per night for 1 room by summing ALL taxes
    // This includes: service charge, GST, VAT, and any other taxes
    let totalTax = 0;
    
    plan.taxes.forEach((tax) => {
      if (isTaxIncluded(tax)) return; // Included tax = already in rate, don't add to per-night tax
      const taxId = tax.TaxId || tax.taxId || tax.TaxID || 0;

      // Check for TaxAmmount first (from tax API calculation - most accurate)
      const taxAmount = parseFloat(
        tax.TaxAmmount || tax.TaxAmount || tax.taxAmount || tax.amount || 0
      );
      
      // Check for TaxValue (from availability API - might be percentage or fixed)
      const taxValue = parseFloat(
        tax.TaxValue || tax.taxValue || tax.value || 0
      );
      
      let calculatedTax = 0;
      
      // For TaxId = 19, TaxValue is typically a fixed amount per room per night
      if (taxId === 19) {
        calculatedTax = taxValue;
      } else if (taxAmount > 0) {
        // If TaxAmmount exists (from tax API), use it
        // TaxAmmount is typically for 1 room for all nights, so divide by nights to get per night
        calculatedTax = nights > 0 ? taxAmount / nights : taxAmount;
      } else if (taxValue > 0) {
        // If only TaxValue exists, calculate based on room price
        // taxValue could be:
        // - Percentage (e.g., 18 for 18% GST)
        // - Fixed amount per night
        // - Rate per unit
        
        // If taxValue is less than 100, likely a percentage
        if (taxValue < 100 && taxValue > 0) {
          // Calculate tax as percentage of room price per night
          calculatedTax = (calculatedPrice * taxValue) / 100;
        } else {
          // Treat as fixed amount per night
          calculatedTax = taxValue;
        }
      }
      
      // Add this tax to the total sum (sums all taxes: service, GST, VAT, etc.)
      if (!isNaN(calculatedTax) && calculatedTax > 0) {
        totalTax += calculatedTax;
      }
    });

    // Round to 2 decimal places
    const roundedTax = Math.round(totalTax * 100) / 100;
    
    return roundedTax;
  };

  // Format date for display
  const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear().toString().slice(-2)}`;
  };

  // Calculate tax when modal opens
  useEffect(() => {
    if (showTaxModal) {
      calculateTax();
    }
  }, [showTaxModal, currentQuantity, plan, room]);

  const calculateTax = async () => {
    setIsLoadingTax(true);
    setTaxError(null);
    
    try {
      // Extract tax data from rate plan
      const taxArray = extractTaxDataFromRatePlan(plan);
      
      if (!taxArray || taxArray.length === 0) {
        setTaxError('No tax information available for this rate plan');
        setIsLoadingTax(false);
        return;
      }

      // Use currentQuantity or default to 1 if no room selected
      const quantity = currentQuantity > 0 ? currentQuantity : 1;
      
      // Build noofroomArray
      const noofroomArray = { "0": quantity };
      
      // Calculate total bedrooms
      const totalBedrooms = (plan.noOfBedRooms || 1) * quantity;
      
      // Calculate total price (price per night * quantity * nights) - same as AvailabilityComponent.jsx
      const totalPrice = calculatedPrice ;

      // Call tax API
      const result = await getTax({
        nights,
        conversionrate: plan.conversionRate || 0,
        noofroomArray,
        totalnumberofbedrooms: totalBedrooms,
        price: totalPrice,
        data: taxArray
      });

      setTaxData(result);
    } catch (error) {
      setTaxError(error.message || 'Failed to calculate tax');
    } finally {
      setIsLoadingTax(false);
    }
  };

  return (
    
    <div
      className={`flex flex-col md:flex-row md:items-start md:justify-between mb-3 border rounded-xl shadow-sm p-3 transition-all hover:shadow-md
        ${isSelected
          ? 'bg-green-50 border-green-500'
          : (plan.secretDeal || hasPromotionFromAvailability || redeemOverrideDiscountPercent > 0)
            ? 'bg-blue-50 border-blue-200'
            : 'bg-white border-gray-200'}
      `}
    >
      {/* ---------- Left: Plan Info ---------- */}
      <div className="flex-1 min-w-0">
        {/* Plan Title & Tags */}
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <h4 className="font-bold text-blue-900 text-sm tracking-tight">
            {plan.name}
          </h4>

          {/* Lowest Rate tag — first plan is the cheapest */}
          {isLowestRate && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-100 border border-emerald-300 text-emerald-800 text-[10px] font-bold whitespace-nowrap">
              <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
              {t('ratePlan.lowestRate')}
            </span>
          )}

          {/* Promotion Badge - Show promotion name instead of "SECRET DEAL" */}
          {/* Check both promotionDiscount.promotionName and plan.promotionName */}
          {(hasPromotionFromAvailability || redeemOverrideDiscountPercent > 0) && (plan.promotionDiscount?.promotionName || plan.promotionName || enhancedPromotionDiscount?.promotionName || membershipTypeName) && (
            <span
              className="inline-flex items-center px-2 py-0.5 rounded-lg text-[10px] font-bold border uppercase tracking-wide"
              style={{
                backgroundColor: 'var(--primary-color, #fbbf24)',
                color: '#fff'
              }}
            >
              <svg
                className="h-3 w-3 mr-0.5"
                style={{ color: '#fff' }}
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              {(() => {
                // Determine display name based on discount source
                let displayName = '';

                if (hasPromotionFromAvailability && redeemOverrideDiscountPercent > 0) {
                  // Both discounts exist - show both names
                  const promotionName = plan.promotionDiscount?.promotionName || plan.promotionName || enhancedPromotionDiscount?.promotionName || '';
                  const promoName = (typeof promotionName === 'string' ? promotionName.trim() : String(promotionName || '')).toUpperCase();
                  const memberName = membershipTypeName ? membershipTypeName.toUpperCase() : '';
                  if (promoName && memberName) {
                    displayName = `${promoName} + ${memberName}`;
                  } else {
                    displayName = promoName || memberName || t('ratePlan.promotion');
                  }
                } else if (hasPromotionFromAvailability) {
                  // Only availabilityApi discount exists - show promotion name
                  const promotionName = plan.promotionDiscount?.promotionName || plan.promotionName || enhancedPromotionDiscount?.promotionName || '';
                  displayName = (typeof promotionName === 'string' ? promotionName.trim() : String(promotionName || '')).toUpperCase();
                } else if (redeemOverrideDiscountPercent > 0) {
                  // Only fetchRedeemOverrideRules discount exists - show membership type name
                  displayName = membershipTypeName ? membershipTypeName.toUpperCase() : t('ratePlan.memberDiscount');
                }

                // Append SECRET DEAL if applicable
                if (plan.secretDeal && displayName) {
                  displayName = `${displayName} + ${t('ratePlan.secretDeal').toUpperCase()}`;
                }

                return displayName || t('ratePlan.promotion');
              })()}
            </span>
          )}

          {/* Secret Deal Badge - Only show if no promotion */}
          {plan.secretDeal && !hasPromotionFromAvailability && redeemOverrideDiscountPercent === 0 && (
            <span
              className="inline-flex items-center px-3 py-1 rounded-tl-xl rounded-br-xl text-xs font-bold shadow uppercase tracking-wider"
              style={{
                backgroundColor: 'var(--primary-color, #facc15)',
                color: '#fff',
                boxShadow: '0 2px 8px 0 rgba(0, 0, 0, 0.15)'
              }}
            >
              <svg
                className="h-4 w-4 mr-1"
                style={{ color: 'var(--primary-color, #1e40af)' }}
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M10 15l-3.09 1.636.588-3.43L5 10.364l3.454-.502L10 6.5l1.546 3.362L15 10.364l-2.498 2.842.588 3.43z" />
              </svg>
              {t('ratePlan.secretDeal')}
            </span>
          )}
        </div>

        {/* Plan Description */}
        <p className="text-sm text-gray-700 leading-relaxed mb-3">
          {/* {plan.description} */}
        </p>
    

        {/* Plan Features */}
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-gray-600 mt-3" style={{ color: 'inherit' }}>
          {/* Booking Policy - Refundable + Included stacked vertically */}
          <div ref={policyTooltipRef} className="relative flex flex-col items-start gap-1.5">
            {/* Refundable Row - Clickable */}
            <div
              className={`flex items-center gap-2 ${hasAnyPolicyData(plan) ? 'cursor-pointer group' : ''}`}
              onClick={() => {
                if (hasAnyPolicyData(plan)) setShowPolicyTooltip(!showPolicyTooltip);
              }}
            >
              {/* Information Icon - only show when policy data exists */}
              {hasAnyPolicyData(plan) && (
                <div className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
              )}
              {(plan.refundStatus == 1 || plan.refundStatus == 2) && (
                plan.refundStatus == 1 ? (
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-50 border border-green-200 rounded-md px-2 py-0.5">
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                    {t('ratePlan.freeCancellation')}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 text-xs rounded-md px-2 py-0.5 bg-gray-100 border border-gray-200">
                    <span className="font-medium text-gray-600">{t('ratePlan.nonRefundable')}</span>
                    <span className="text-gray-300">·</span>
                    <span className="inline-flex items-center gap-0.5 font-semibold text-green-700">
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                      {t('ratePlan.bestPrice')}
                    </span>
                  </span>
                )
              )}
              {hasAnyPolicyData(plan) && (
                <svg className="w-3 h-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              )}
            </div>

            {/* What's Included - Below Refundable, aligned with text */}
            {plan.included && (() => {
              const includedText = Array.isArray(plan.included) ? plan.included.join('\n') : String(plan.included);
              const items = includedText.split(/[\n|]+/).map(s => s.trim().replace(/[|]+$/, '').trim()).filter(Boolean);
              if (items.length === 0) return null;
              return (
                <div
                  className="flex items-center gap-1.5 cursor-pointer group"
                  onClick={(e) => { e.stopPropagation(); setShowIncludedModal(true); }}
                >
                  <svg className="w-3.5 h-3.5 text-green-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-xs font-semibold text-green-700 group-hover:text-green-900">
                    {t('ratePlan.whatsIncluded')}
                  </span>
                  <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              );
            })()}

            {/* Policy Details - Modal */}
            {showPolicyTooltip && (
              <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center p-4" onClick={(e) => { e.stopPropagation(); setShowPolicyTooltip(false); }}>
                <div className="bg-white rounded-xl max-w-md w-full max-h-[80vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-between p-4 border-b border-gray-200">
                    <h5 className="text-base font-bold text-gray-900">{t('ratePlan.policyDetails')}</h5>
                    <button onClick={(e) => { e.stopPropagation(); setShowPolicyTooltip(false); }} className="text-gray-400 hover:text-gray-600 p-1">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  </div>
                  <div className="p-4 space-y-4">
                    {plan.cancellationPolicy && !Array.isArray(plan.cancellationPolicy) && String(plan.cancellationPolicy).trim() && (
                      <div>
                        <h6 className="text-sm font-bold text-blue-900 mb-2">{t('ratePlan.cancellationPolicy')}</h6>
                        <ul className="text-xs text-gray-600 space-y-1 list-disc list-inside">
                          {String(plan.cancellationPolicy).split('|').map((item, i) => item.trim() && <li key={i}>{item.trim()}</li>)}
                        </ul>
                      </div>
                    )}
                    {plan.bookingPolicy && !Array.isArray(plan.bookingPolicy) && String(plan.bookingPolicy).trim() && (
                      <div>
                        <h6 className="text-sm font-bold text-blue-900 mb-2">{t('ratePlan.bookingPolicy')}</h6>
                        <ul className="text-xs text-gray-600 space-y-1 list-disc list-inside">
                          {String(plan.bookingPolicy).split('|').map((item, i) => item.trim() && <li key={i}>{item.trim()}</li>)}
                        </ul>
                      </div>
                    )}
                    {plan.noShowPolicy && !Array.isArray(plan.noShowPolicy) && String(plan.noShowPolicy).trim() && (
                      <div>
                        <h6 className="text-sm font-bold text-blue-900 mb-2">{t('ratePlan.noShowPolicy')}</h6>
                        <ul className="text-xs text-gray-600 space-y-1 list-disc list-inside">
                          {String(plan.noShowPolicy).split('|').map((item, i) => item.trim() && <li key={i}>{item.trim()}</li>)}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>


          {/* Breakfast — flag first, else infer from name; unknown → hide (don't contradict name) */}
          {getBreakfastStatus(plan) !== null && (
          <div className="flex items-center gap-1.5 self-start mt-0.5">
            {getBreakfastStatus(plan) ? (
              <>
                <svg
                  className="h-3.5 w-3.5 text-green-600 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span
                  className="text-xs text-green-700 font-medium"
                >
                  {t('ratePlan.breakfastIncluded')}
                </span>
              </>
            ) : (
              <>
                <svg
                  className="h-4 w-4 text-gray-400"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 
                       1.414L8.586 10l-1.293 1.293a1 1 0 101.414 
                       1.414L10 11.414l1.293 1.293a1 1 0 
                       001.414-1.414L11.414 10l1.293-1.293a1 
                       1 0 00-1.414-1.414L10 8.586 8.707 
                       7.293z"
                  />
                </svg>
                <span className="text-gray-500">{t('ratePlan.noBreakfastIncluded')}</span>
              </>
            )}
          </div>
          )}

          {/* Included Modal */}
          {showIncludedModal && plan.included && (() => {
            const includedText = Array.isArray(plan.included) ? plan.included.join('\n') : String(plan.included);
            const items = includedText.split(/[\n|]+/).map(s => s.trim().replace(/[|]+$/, '').replace(/&amp;/g, '&').trim()).filter(Boolean);
            return (
              <div
                className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center p-4"
                onClick={(e) => { e.stopPropagation(); setShowIncludedModal(false); }}
              >
                <div
                  className="bg-white rounded-xl max-w-md w-full max-h-[80vh] overflow-y-auto shadow-2xl"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex items-center justify-between p-4 border-b border-gray-200">
                    <h5 className="text-base font-bold text-gray-900 flex items-center gap-2">
                      ✨ {t('ratePlan.whatsIncluded') || "What's Included"}
                    </h5>
                    <button
                      onClick={(e) => { e.stopPropagation(); setShowIncludedModal(false); }}
                      className="text-gray-400 hover:text-gray-600 p-1"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  <div className="p-4">
                    <ul className="space-y-2">
                      {items.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-gray-700 leading-relaxed">
                          <svg className="w-4 h-4 mt-0.5 text-green-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span>{item.replace(/&amp;/g, '&')}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Guest Icons - Show between No Breakfast and Select Rooms */}
          {guests.adults > 0 && (
            <div className="flex flex-col items-start text-gray-700 self-start mt-0.5">
              {/* Adult + Child — compact inline guest info (no bordered pill) */}
              <div className="inline-flex items-center gap-2">
                {/* Adults */}
                <div className="flex items-center gap-1.5">
                  <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <span className="text-sm text-gray-700 leading-none"><span className="font-semibold">{guests.adults}</span> {guests.adults === 1 ? t('search.adult') : t('search.adults')}</span>
                </div>
                {/* Children — only if > 0 */}
                {guests.children > 0 && (
                  <>
                    <span className="text-gray-400 text-sm font-medium">+</span>
                    <div className="flex items-center gap-1.5">
                      <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      <span className="text-sm text-gray-700 leading-none"><span className="font-semibold">{guests.children}</span> {guests.children === 1 ? t('search.child') : t('search.children')}</span>
                    </div>
                  </>
                )}
              </div>
              {/* Age - Below icons */}
              {guests.children > 0 && childAges.length > 0 && (
                <span className="text-[10px] mt-0.5 text-gray-600">
                  (Age: {childAges.join(', ')})
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ---------- Right: Price & Quantity ---------- */}
      <div className="mt-3 md:mt-0 md:ml-4 flex-shrink-0 w-full md:w-auto">
        {/* Price + Quantity Row */}
        <div className="mb-3">
          {hasAnyDiscount ? (
            <div>
              {/* Hide price for secret deals when not logged in */}
              {!(plan.secretDeal && !isLoggedIn) && (
              <>
              {/* Discount Badge - Top Right */}
              {originalPrice > calculatedPrice && (
                <div className="flex justify-end mb-1">
                  <span className="bg-green-100 text-green-700 text-xs font-semibold px-2 py-0.5 rounded">
                    {originalPrice > 0 ? Math.round(((originalPrice - calculatedPrice) / originalPrice) * 100) : 0}% {t('ratePlan.off')}
                  </span>
                </div>
              )}
              {/* Price and +/- buttons in same row */}
              <div className="flex items-center justify-end gap-4 mb-2">
                {/* Price Info */}
                <div className="text-right">
                  {/* Price Row - Original crossed + Discounted inline */}
                  <div className="flex items-baseline justify-end gap-2">
                    {originalPrice > calculatedPrice && (
                      <span className="text-sm text-gray-400 line-through">
                        {displayCurrency}{(originalPrice || 0).toLocaleString(undefined, { minimumFractionDigits: originalPrice % 1 !== 0 ? 2 : 0, maximumFractionDigits: 2 })}
                      </span>
                    )}
                    <span className="text-xl font-bold text-gray-900">
                      {displayCurrency}{(calculatedPrice || 0).toLocaleString(undefined, { minimumFractionDigits: calculatedPrice % 1 !== 0 ? 2 : 0, maximumFractionDigits: 2 })}
                    </span>
                    <span className="text-sm font-normal text-gray-600">{t('ratePlan.perNight')}</span>
                  </div>
                  {hasTax() && calculateTaxPerNight() > 0 && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowTaxModal(true);
                      }}
                      className="text-xs text-gray-500 hover:text-blue-600 cursor-pointer border-b border-dashed border-gray-400 hover:border-blue-600"
                    >
                      + {displayCurrency}{calculateTaxPerNight().toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {t('ratePlan.taxes')}
                    </button>
                  )}
                  {/* Tax included in rate → show inclusive note instead of an add-on tax line */}
                  {hasIncludedTax(plan.taxes) && (
                    <div className="text-[10px] text-green-700 font-medium">
                      Price includes all taxes and fees
                    </div>
                  )}
                  {hasTax() && !hasIncludedTax(plan.taxes) && (
                    <div className="text-[10px] text-gray-500">
                      {nights > 1 ? t('ratePlan.nightsInclTaxes', { n: nights }) : t('ratePlan.nightInclTaxes')}
                    </div>
                  )}
                </div>

                {/* +/- Buttons */}
                {!(plan.secretDeal && !isLoggedIn) && (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => updateRoomQuantity(room.id, plan.id, Math.max(0, currentQuantity - 1), calculatedPrice)}
                      disabled={currentQuantity === 0}
                      className="w-8 h-8 flex items-center justify-center avablity-btn font-semibold text-white rounded disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      -
                    </button>
                    <span className="w-8 h-8 flex items-center justify-center text-sm font-medium text-gray-900 bg-gray-50 border border-gray-200 rounded">
                      {currentQuantity}
                    </span>
                    <button
                      onClick={() => updateRoomQuantity(room.id, plan.id, currentQuantity + 1, calculatedPrice)}
                      disabled={isMaxReached}
                      className="w-8 h-8 flex items-center justify-center avablity-btn rounded text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      +
                    </button>
                  </div>
                )}
              </div>
              </>
              )}



              {/* Member Rate Applied Badge - Only for logged-in users */}
              {isLoggedIn && (loyaltyDiscountValue > 0 || plan.secretDeal) && (
                <div className="mt-2 text-right">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold bg-green-50 text-green-700 border border-green-200">
                    ✓ {t('ratePlan.memberRateApplied')}
                  </span>
                </div>
              )}
            </div>
          ) : plan.secretDeal && !isLoggedIn ? (
            /* Hide price section completely for secret deal when user is not logged in */
            null
          ) : plan.secretDeal && isLoggedIn ? (
            /* Show price for secret deal when user is logged in */
            <div className="text-right">
              {hasTax() ? (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowTaxModal(true);
                  }}
                  className="text-xl font-bold hover:underline cursor-pointer inline-flex items-center gap-1"
                  style={{ color: 'var(--primary-color, #1e40af)' }}
                >
                  {roundCurrency(
                    calculatedPrice * (1 - (plan.memberDiscount || 0))
                  ).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{displayCurrency}
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              ) : (
                <span className="text-xl font-bold" style={{ color: 'var(--primary-color, #1e40af)' }}>
                  {roundCurrency(
                    calculatedPrice * (1 - (plan.memberDiscount || 0))
                  ).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{displayCurrency}
                </span>
              )}
              {hasTax() && (() => {
                const taxPerNight = calculateTaxPerNight();
                return taxPerNight > 0 ? (
                  <div className="mt-0.5">
                    <p className="text-xs text-gray-600">
                      + {displayCurrency}{taxPerNight.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                    </p>
                    <p className="text-[10px] text-gray-500 mt-0.5">{t('ratePlan.taxesAndCharges')}</p>
                  </div>
                ) : null;
              })()}
            </div>
          ) : (
            <div>
              {/* Price and +/- buttons in same row */}
              <div className="flex items-center justify-end gap-4 mb-2">
                {/* Price Info */}
                <div className="text-right">
                  <div className="text-xl font-bold text-gray-900">
                    {displayCurrency}{(calculatedPrice || 0).toLocaleString(undefined, { minimumFractionDigits: calculatedPrice % 1 !== 0 ? 2 : 0, maximumFractionDigits: 2 })}<span className="text-sm font-normal text-gray-600">{t('ratePlan.perNight')}</span>
                  </div>
                  {hasTax() && calculateTaxPerNight() > 0 && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowTaxModal(true);
                      }}
                      className="text-xs text-gray-500 hover:text-blue-600 cursor-pointer border-b border-dashed border-gray-400 hover:border-blue-600"
                    >
                      + {displayCurrency}{calculateTaxPerNight().toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {t('ratePlan.taxes')}
                    </button>
                  )}
                  {/* Tax included in rate → show inclusive note instead of an add-on tax line */}
                  {hasIncludedTax(plan.taxes) && (
                    <div className="text-[10px] text-green-700 font-medium">
                      Price includes all taxes and fees
                    </div>
                  )}
                  {hasTax() && !hasIncludedTax(plan.taxes) && (
                    <div className="text-[10px] text-gray-500">
                      {nights > 1 ? t('ratePlan.nightsInclTaxes', { n: nights }) : t('ratePlan.nightInclTaxes')}
                    </div>
                  )}
                </div>

                {/* +/- Buttons */}
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => updateRoomQuantity(room.id, plan.id, Math.max(0, currentQuantity - 1), calculatedPrice)}
                    disabled={currentQuantity === 0}
                    className="w-8 h-8 flex items-center justify-center avablity-btn font-semibold text-white rounded disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    -
                  </button>
                  <span className="w-8 h-8 flex items-center justify-center text-sm font-medium text-gray-900 bg-gray-50 border border-gray-200 rounded">
                    {currentQuantity}
                  </span>
                  <button
                    onClick={() => updateRoomQuantity(room.id, plan.id, currentQuantity + 1, calculatedPrice)}
                    disabled={isMaxReached}
                    className="w-8 h-8 flex items-center justify-center avablity-btn rounded text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    +
                  </button>
                </div>
              </div>



              {/* Member Rate Applied Badge - Only for logged-in users */}
              {isLoggedIn && (loyaltyDiscountValue > 0 || plan.secretDeal) && (
                <div className="mt-2 text-right">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold bg-green-50 text-green-700 border border-green-200">
                    ✓ {t('ratePlan.memberRateApplied')}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Secret Deal Unlock Button - Only show when user is not logged in */}
        {plan.secretDeal && !isLoggedIn && (
          <div className="flex justify-end">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  trackMemberUnlock(accommodationDetail?.AccommodationId || '', { ratePlanName: plan.name || plan.RatePlanName || '', roomName: room?.RoomName || room?.name || '' });
                  if (onOpenLogin) {
                    onOpenLogin();
                  }
                }}
                className="group relative px-6 py-3 flex items-center justify-center gap-2 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 hover:opacity-90"
                style={{ backgroundColor: 'var(--secondary-color, #3b82f6)' }}
              >
                <svg
                  className="w-5 h-5 transition-transform group-hover:scale-110"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2.5}
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  />
                </svg>
                <span className="text-base tracking-wide">{t('ratePlan.unlockMemberRate')}</span>
                <svg
                  className="w-4 h-4 transition-transform group-hover:translate-x-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 7l5 5m0 0l-5 5m5-5H6"
                  />
                </svg>
              </button>
          </div>
        )}
      </div>

      {/* Tax Modal */}
      {showTaxModal && (
        <div 
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50" 
          onClick={() => setShowTaxModal(false)}
          style={{ zIndex: 9999 }}
        >
          <div 
            className="bg-white rounded-lg shadow-2xl max-w-xs w-full mx-4 max-h-[60vh] overflow-hidden flex flex-col" 
            onClick={(e) => e.stopPropagation()}
            style={{ zIndex: 10000 }}
          >
            {/* Header */}
            <div 
              className="card-head text-white px-3 py-2 rounded-t-lg"
              style={{ backgroundColor: 'var(--primary-color)', background: 'var(--primary-color)' }}
            >
              <h3 className="text-xs font-semibold">
                {t('ratePlan.rateForNights', { nights: nights, guests: guests.adults + guests.children, quantity: currentQuantity > 0 ? currentQuantity : 1 })}
              </h3>
            </div>

            {/* Content */}
            <div className="p-3 overflow-y-auto flex-1">
              {isLoadingTax ? (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 mx-auto" style={{ borderColor: 'var(--primary-color)' }}></div>
                  <p className="mt-2 text-xs text-gray-600">{t('ratePlan.calculatingTaxes')}</p>
                </div>
              ) : taxError ? (
                <div className="text-center py-4">
                  <p className="text-xs text-red-600">{taxError}</p>
                  <button
                    onClick={() => setShowTaxModal(false)}
                    className="mt-2 px-3 py-1.5 text-xs text-white rounded-lg transition-colors"
                    style={{ backgroundColor: 'var(--primary-color)', background: 'var(--primary-color)' }}
                    onMouseEnter={(e) => {
                      e.target.style.opacity = '0.9';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.opacity = '1';
                    }}
                  >
                    {t('ratePlan.close')}
                  </button>
                </div>
              ) : taxData ? (
                <>
                  {/* Booking Summary */}
                  <div className="mb-3">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="text-[10px] text-gray-600">{t('ratePlan.date')}</p>
                        <p className="text-xs font-medium text-gray-900">
                          {selectedDates?.checkIn ? formatDate(selectedDates.checkIn) : 'N/A'}
                        </p>
                      </div>
                      <div className="text-right">
                        {(() => {
                          const quantity = currentQuantity > 0 ? currentQuantity : 1;
                          return (
                            <>
                              {hasAnyDiscount && (() => {
                                const quantity = currentQuantity > 0 ? currentQuantity : 1;
                                const totalOriginalPrice = originalPrice * quantity ;
                                const totalCalculatedPrice = calculatedPrice * quantity ;
                                return (
                                  <>
                                    <p className="text-[10px] line-through text-gray-500">
                                      {displayCurrency}{(totalOriginalPrice || 0).toLocaleString()}
                                    </p>
                                    <p className="text-sm font-bold text-gray-900">
                                      {displayCurrency}{(totalCalculatedPrice || 0).toLocaleString()}
                                    </p>
                                    <p className="text-[10px] text-red-600 font-medium">
                                      Save {displayCurrency}{((totalOriginalPrice - totalCalculatedPrice) || 0).toLocaleString()}
                                    </p>
                                  </>
                                );
                              })()}
                              {!hasAnyDiscount && (() => {
                                const quantity = currentQuantity > 0 ? currentQuantity : 1;
                                const totalCalculatedPrice = calculatedPrice * quantity ;
                                return (
                                  <p className="text-sm font-bold text-gray-900">
                                    {displayCurrency}{(totalCalculatedPrice || 0).toLocaleString()}
                                  </p>
                                );
                              })()}
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  </div>

                  {/* Room Taxes */}
                  <div className="mb-3 border-t border-gray-200 pt-2">
                    <h4 className="text-xs font-semibold text-gray-900 mb-1.5">{t('ratePlan.roomTaxes')}</h4>
                    <div className="space-y-2">
                      {(() => {
                        // Response structure: { message: '...', taxesBreakDown: [...] }
                        const taxes = taxData.taxesBreakDown || taxData.taxes || taxData.Taxes || taxData.taxBreakdown || taxData.data?.taxes || [];
                        const totalTax = taxData.totalTax || taxData.TotalTax || taxData.totalTaxAmount || taxData.TotalTaxAmount || 0;
                        
                        if (Array.isArray(taxes) && taxes.length > 0) {
                          const quantity = currentQuantity > 0 ? currentQuantity : 1;
                          return taxes.map((tax, index) => {
                            // Use TaxValue if TaxId = 19, otherwise use TaxAmmount
                            const taxId = tax.TaxId || tax.taxId || tax.TaxID || 0;
                            let taxAmount = taxId === 19 
                              ? (tax.TaxValue || tax.taxValue || 0)
                              : (tax.TaxAmmount || tax.TaxAmount || tax.taxAmount || 0);
                            
                            // TaxId 19: TaxValue used as-is (no quantity multiplication)
                            
                            return (
                              <div key={index} className="flex justify-between items-center py-0.5">
                                <span className="text-[10px] text-gray-700">
                                  {tax.TaxName || tax.taxName || tax.name || 'Tax'}
                                </span>
                                <span className="text-[10px] font-medium text-gray-900">
                                  {displayCurrency}{(isNaN(parseFloat(taxAmount)) ? 0 : parseFloat(taxAmount)).toLocaleString()}
                                </span>
                              </div>
                            );
                          });
                        } else if (totalTax > 0) {
                          // Show total tax if breakdown not available
                          return (
                            <div className="flex justify-between items-center py-0.5">
                              <span className="text-[10px] text-gray-700">{t('ratePlan.gst')}</span>
                              <span className="text-[10px] font-medium text-gray-900">
                                {displayCurrency}{parseFloat(totalTax).toLocaleString()}
                              </span>
                            </div>
                          );
                        } else {
                          return (
                            <div className="text-[10px] text-gray-500">{t('ratePlan.noTaxInfo')}</div>
                          );
                        }
                      })()}
                    </div>
                  </div>

                  {/* Total */}
                  <div className="border-t border-gray-200 pt-2 mb-2">
                    <div className="flex justify-between items-center mb-1">
                      <h4 className="text-sm font-bold text-gray-900">{t('ratePlan.total')}</h4>
                      <div className="text-right">
                        <p className="text-sm font-bold text-gray-900">
                          {displayCurrency}{(() => {
                            // Calculate total: base price + all taxes (same as AvailabilityComponent.jsx)
                            const quantity = currentQuantity > 0 ? currentQuantity : 1;
                            const basePrice = calculatedPrice * quantity ;
                            const taxes = taxData.taxesBreakDown || taxData.taxes || [];
                            let totalTaxAmount = 0;
                            
                            if (Array.isArray(taxes) && taxes.length > 0) {
                              totalTaxAmount = taxes.reduce((sum, tax) => {
                                if (isTaxIncluded(tax)) return sum; // Included tax = already in rate, don't add
                                const taxId = tax.TaxId || tax.taxId || tax.TaxID || 0;
                                let taxAmount = 0;

                                // For TaxId = 19, use TaxValue as-is (no quantity multiplication)
                                // For other taxes, use TaxAmmount (API already calculated with quantity and nights)
                                if (taxId === 19) {
                                  const taxValue = parseFloat(tax.TaxValue || tax.taxValue || 0);
                                  taxAmount = taxValue;
                                } else {
                                  taxAmount = parseFloat(tax.TaxAmmount || tax.TaxAmount || tax.taxAmount || tax.amount || 0);
                                }

                                return sum + (isNaN(taxAmount) ? 0 : taxAmount);
                              }, 0);
                            } else {
                              const rawTax = parseFloat(taxData.totalTax || taxData.TotalTax || taxData.totalTaxAmount || 0);
                              totalTaxAmount = isNaN(rawTax) ? 0 : rawTax;
                            }
                            
                            // Calculate final total: base price + taxes
                            const finalTotal = basePrice + totalTaxAmount;
                            return parseFloat(finalTotal).toLocaleString();
                          })()}
                        </p>
                        <p className="text-[10px] text-gray-500">{t('ratePlan.taxInclusive')}</p>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-4">
                  <p className="text-xs text-gray-600">{t('ratePlan.noTaxInfo')}</p>
                  <button
                    onClick={() => setShowTaxModal(false)}
                    className="mt-2 px-3 py-1.5 text-xs text-white rounded-lg transition-colors"
                    style={{ backgroundColor: 'var(--primary-color)', background: 'var(--primary-color)' }}
                    onMouseEnter={(e) => {
                      e.target.style.opacity = '0.9';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.opacity = '1';
                    }}
                  >
                    {t('ratePlan.close')}
                  </button>
                </div>
              )}
            </div>
            
            {/* Footer - Close Button */}
            {taxData && !isLoadingTax && !taxError && (
              <div className="border-t border-gray-200 px-3 py-2">
                <button
                  onClick={() => setShowTaxModal(false)}
                  className="w-full font-semibold py-1.5 text-xs rounded-lg transition-colors border-2 bg-transparent"
                  style={{ borderColor: 'var(--primary-color)', color: 'var(--primary-color)' }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = 'var(--primary-color)';
                    e.target.style.color = '#fff';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = 'var(--primary-color)';
                  }}
                >
                  {t('ratePlan.close')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Breakfast Modal */}
      {showBreakfastModal && (
        <div 
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50" 
          onClick={() => setShowBreakfastModal(false)}
          style={{ zIndex: 9999 }}
        >
          <div 
            className="bg-white rounded-lg shadow-2xl max-w-xs w-full mx-4 max-h-[50vh] overflow-hidden flex flex-col" 
            onClick={(e) => e.stopPropagation()}
            style={{ zIndex: 10000 }}
          >
            {/* Header */}
            <div 
              className="card-head text-white px-3 py-2 rounded-t-lg flex items-center justify-between"
              style={{ backgroundColor: 'var(--primary-color)', background: 'var(--primary-color)' }}
            >
              <h3 className="text-xs font-semibold">{t('ratePlan.breakfastIncluded')}</h3>
              <button
                onClick={() => setShowBreakfastModal(false)}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content - Scrollable */}
            <div className="p-3 overflow-y-auto flex-1">
              {typeof plan.breakFastDescription === 'string' && plan.breakFastDescription.includes('|') ? (
                <ul className="text-xs text-gray-700 space-y-1.5 list-disc list-inside">
                  {plan.breakFastDescription.split('|').map((item, index) => (
                    <li key={index} className="leading-relaxed">{item.trim()}</li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-gray-700 leading-relaxed">{plan.breakFastDescription || t('ratePlan.breakfastDetails')}</p>
              )}
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 px-3 py-2">
              <button
                onClick={() => setShowBreakfastModal(false)}
                className="w-full font-semibold py-1.5 text-xs rounded-lg transition-colors border-2 bg-transparent"
                style={{ borderColor: 'var(--primary-color)', color: 'var(--primary-color)' }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'var(--primary-color)';
                  e.target.style.color = '#fff';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = 'var(--primary-color)';
                }}
              >
                {t('ratePlan.close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RatePlanCard;
